import * as Ably from 'ably';
import { DataServices } from '../utils/DataServices';
import { DataTools } from '../utils/DataTools';
import { LangServices } from '../utils/LangServices';
import { LogicEngine } from '../LogicEngine/LogicEngine';
import { PageEngine } from '../PageParser/PageEngine';

// Library for Events that are strongly typed
// see https://github.com/vitaly-t/sub-events
import { SubEvent } from 'sub-events';

// Reference to the package version.  Should be bumped with
// 'npm version minor' command on each publish.
const { version } = require('../../package.json');

export interface RemoteAgentConnectionType
{
	version: string;   // version of the PlatformShared
	session_id: string;
	device_id: string;
	user: string;
	pid: string;
	ip: string;
	env: string;
	project: string;
	start: number; // Start of the session in getTime format
	page: string; // Current page (last page loaded by route name)
	status: string;
};

export interface RemoteAgentWatchFunction
{
	(connection: RemoteAgentConnectionType): void;
}

export interface RemoteAgentPageStatus
{
	version: string;
	session_id: string;
	user: string;
	pid: string;
	ip: string;
	env: string;
	project: string;
	local_time: Date;
	local_offset: number;
	page: string;
	status: string;
};

export interface RemoteAgentPageUpdateCallback
{
	(connection: RemoteAgentPageStatus): void;
}

export interface RemoteAgentLoginEvent
{
	login_type: 'participant' | 'user';
	username?: string; // Set for user login
	password?: string; // Set for user login or a participant login.
	pid?: string; // Set for a participant based login
}

export interface RemoteAgentLoginCallback
{
	(loginData: RemoteAgentLoginEvent): Promise<boolean>;
}

export interface RemoteAgentNavigateEvent
{
	nav_type: 'page';
	page: string;
}

export interface RemoteAgentNavigateCallback
{
	(loginData: RemoteAgentNavigateEvent): Promise<boolean>;
}

export interface RemoteAgentMonitorEvent
{
	monitor_type: 'debug' | 'pages' | 'off';
}

/**
@class RemoteAgent

This is a debugging system that uses Pusher.com for sending information
about the session and allowing a remote command and control system to
issue commands and make adjustments.

There are 2 pub/sub channels

control - Used to send commands from the "master" to the apps / portal
so that the master can issue commands.  Commands are sent with a session_id
that must match the session_id assigned in Logic Engine or they are ignored.

announce - Used by the "clients" to send their status or respond to a command.
The "master" should listen on this one (which is done automatically in the
watchForConnections function)

Events and methods that the Mobile App and EDC Portal can implement:
Events that start with "ev" are optional and can be "subscribed to".
Callbacks are required and must be set with RemoteAgent.setHandler* functions.

evRemoteSetProject - Called when the master sets the project.  Optional

evRemoteSetEnvironment - Called when the master sets the environment.  Otional.


*/

export class RemoteAgent
{
	evRemoteSetProject: SubEvent<string> = new SubEvent();
	evRemoteSetEnvironment: SubEvent<string> = new SubEvent();

	/** Handlers for sending commands to the mobile or web implementation. */
	handlerLogin: RemoteAgentLoginCallback | undefined;
	handlerNavigate: RemoteAgentNavigateCallback | undefined;

	cbPageUpdate: RemoteAgentPageUpdateCallback | undefined;
	cbPing: RemoteAgentWatchFunction | undefined;

	/** Reference to the current page being executed */
	currentPage: PageEngine | undefined;

	/**
	 Should be a single instance access function so that we can share common references without
	 having to reload configuration files more than once
	 */
	static _instance: RemoteAgent;

	/** The channel that we use for command and control */
	static controlChannel = 'laina-cc';
	static announceChannel = 'laina-an';
	static commandWho = 'who';
	static commandSetEnv = 'setenv';
	static commandSetProject = 'setproj';
	static commandLogin = 'login';
	static commandGetLogic = 'getlogic';
	static commandSetLogic = 'setlogic';
	static commandNavigate = 'nav';
	static commandMonitor = 'monitor';

	/** Change to false to globally disable the remote agent */
	// typeof process returns "object" in nodejs but will return undefined on browser or React-Native
	// So enabled will be false by default for command line scripts.
	static enabled = (typeof process !== 'object');
	static wantUpdates = false;

	/* Display tracking information in the console */
	static debug = false;

	/** Status text that can be updated as needed for debugging */
	status: string = 'ready';

	/** Reference to the pusher object.
	@see https://dashboard.pusher.com/apps/1324608/getting_started
	*/
	ably: Ably.Realtime | undefined;
	cc_chan: Ably.Types.RealtimeChannelCallbacks | undefined;
	an_chan: Ably.Types.RealtimeChannelCallbacks | undefined;

	/** True if the remote agent is running already */
	isRunning: boolean = false;
	startTime: Date = new Date();

	commandNum: number = 1000;
	commandCallback: { [num: number]: any } = {};
	timerCallback: { [num: number]: any } = {};

	incomingCommands: { [name: string]: CallableFunction } = {};

	/**
	 * Get data service instance
	 * @returns instance for data service
	 */
	static instance(): RemoteAgent
	{
		if (RemoteAgent._instance) return RemoteAgent._instance;
		RemoteAgent._instance = new RemoteAgent();
		return RemoteAgent._instance;
	}

	constructor()
	{
		if (RemoteAgent.enabled)
		{
			const key = 'xBxygQ.XJ-oxg:rRvZpjY9k3plTEhLb2nCiq9aXONIADyz2opVmwCOqjk';
			this.ably = new Ably.Realtime(key);

			this.incomingCommands[RemoteAgent.commandSetEnv] = this.respondToSetEnv.bind(this);
			this.incomingCommands[RemoteAgent.commandWho] = this.respondToCommandHeartbeat.bind(this);
			this.incomingCommands[RemoteAgent.commandLogin] = this.respondToLogin.bind(this);
			this.incomingCommands[RemoteAgent.commandGetLogic] = this.respondToGetLogic.bind(this);
			this.incomingCommands[RemoteAgent.commandSetLogic] = this.respondToSetLogic.bind(this);
			this.incomingCommands[RemoteAgent.commandNavigate] = this.respondToNavigate.bind(this);
			this.incomingCommands[RemoteAgent.commandMonitor] = this.respondToMonitor.bind(this);
		}
	}

	/** Set the handler for login events. */
	setHandlerLogin(cbLogin: RemoteAgentLoginCallback)
	{
		this.handlerLogin = cbLogin;
	}

	/** Set the handler for navigate events. */
	setHandlerNavigate(cbNav: RemoteAgentNavigateCallback)
	{
		this.handlerNavigate = cbNav;
	}

	/** Set the current status of this connection.  Can be used for anything, will update the announce channcel. */
	setStatus(newStatus: string)
	{
		this.status = newStatus;
		this.ping();
	}

	stopRemoteAgent()
	{
		if (!this.ably) return false;
		this.ably.connection.close();
	}

	/** Startup the remote agent class which will connect to the pusher service.
	*/
	startRemoteAgent()
	{
		if (!RemoteAgent.enabled) return false;
		if (!this.ably) return false;

		if (this.isRunning) return;
		this.isRunning = true;

		this.ably.connection.on('connected', () =>
		{
			if (RemoteAgent.debug)
				console.log('Connected to Ably!');
		});

		this.cc_chan = this.ably.channels.get(RemoteAgent.controlChannel);
		this.cc_chan.subscribe((message: Ably.Types.Message) =>
		{

			/** All messages arrive with a command type in 'name' and then the data package */
			if (message && message.data)
			{
				const obj = DataTools.decodeObject(message.data);

				// console.log("Debug Incoming[", message.name, "] [", obj.session_id, " vs ", LogicEngine.currentSessionID, "]");

				// If a target session is specified then check it against our session.
				// don't process messages for other sessions.
				if (obj.session_id && obj.session_id != LogicEngine.currentSessionID)
					return;

				if (this.incomingCommands[message.name])
					this.incomingCommands[message.name](obj);
				else
				{
					if (RemoteAgent.debug)
						console.log("RECEIVE UNKNOWN AUTOMATION [", message.name, "] = ", obj);
				}
			} else
			{
				if (RemoteAgent.debug)
					console.log("RECEIVE MESSAGE:", message);
			}


		});

		this.an_chan = this.ably.channels.get(RemoteAgent.announceChannel);
	}

	/** Send an announcement that we are available */
	async ping()
	{
		if (RemoteAgent.debug)
			console.log("Remote Agent: Sending ping");

		if (!RemoteAgent.enabled) return;
		if (!this.an_chan)
		{
			console.error("Ably: Missing AN Chan");
		} else
		{
			this.an_chan.publish('ping', DataTools.encodeObjectCompressed({
				version,
				session_id: LogicEngine.currentSessionID,
				device_id: LogicEngine.currentDeviceId,
				user: LogicEngine.currentUser,
				pid: LogicEngine.currentParticipantID,
				ip: LogicEngine.currentIpAddress,
				env: DataServices._environment,
				project: DataServices._currentProject,
				start: this.startTime.getTime(),
				page: LogicEngine.currentPageShowing,
				status: this.status
			}))
		}

		return true;
	}

	/** Send an announcement that we are working with a page load/finish */
	async pageStatus(page_name: string, page_status: string)
	{
		if (!RemoteAgent.enabled) return;

		if (RemoteAgent.debug)
			console.log("Remote Agent: Sending page update:", page_name, page_status);

		if (!this.an_chan)
		{
			console.error("Ably: Missing AN Chan");
		} else
		{
			this.an_chan.publish('ping', DataTools.encodeObjectCompressed(<RemoteAgentPageStatus>{
				version,
				session_id: LogicEngine.currentSessionID,
				user: LogicEngine.currentUser,
				pid: LogicEngine.currentParticipantID,
				ip: LogicEngine.currentIpAddress,
				env: DataServices._environment,
				project: DataServices._currentProject,
				page: LogicEngine.currentPageShowing,
				local_time: new Date(),
				local_offset: new Date().getTimezoneOffset(),
				status: page_status,
			}))
		}

		return true;
	}


	async reportMissingLang(strText: string)
	{
		if (!RemoteAgent.enabled) return;
		if (!this.an_chan)
		{
			console.error("Ably: Missing AN Chan");
		} else
		{
			this.an_chan.publish('miss', DataTools.encodeObjectCompressed(<any>{
				version,
				session_id: LogicEngine.currentSessionID,
				pid: LogicEngine.currentParticipantID,
				page: LogicEngine.currentPageShowing,
				strText,
				lang: LangServices.currentLanguage.code
			}));
		}
	}


	/** Setup a callback whenever there is an annoucement from a remote */
	watchForConnections()
	{
		this.an_chan?.subscribe((message) =>
		{
			if (message.name === 'ack')
			{
				//  Ack received from the test device that a command was procesed
				//  remote the timeout timer and pass the data to callback function which is the resolve on the original promise
				const obj = DataTools.decodeObject(message.data);
				const cb = this.commandCallback[obj.id];
				clearTimeout(this.timerCallback[obj.id]);
				delete this.commandCallback[obj.id];
				delete this.timerCallback[obj.id];
				if (cb)
					cb(obj);
			} else
				if (message.name === 'ping')
				{
					const obj = DataTools.decodeObject(message.data);
					if (obj.session_id === LogicEngine.currentSessionID) return;
					if (this.cbPing) this.cbPing(obj);
				} else if (message.name === 'page')
				{
					const obj = DataTools.decodeObject(message.data);
					if (obj.session_id === LogicEngine.currentSessionID) return;
					if (this.cbPageUpdate) this.cbPageUpdate(obj);
				} else
				{
					if (RemoteAgent.debug)
						console.log("UNKNOWN AN MESSAGE:", message);
				}
		});
	}

	/** Handler for the Navigate remote command.
	Will call the login handler that was set and send the result back.*/
	respondToNavigate(obj: RemoteAgentNavigateEvent)
	{
		RemoteAgent.wantUpdates = true;

		if (this.handlerNavigate)
		{
			if (RemoteAgent.debug)
				console.log("RemoteAgent: Received navigate request:", obj);

			let p: any = this.handlerNavigate(obj);
			if (p && p.then)
			{
				p.then((result: any) =>
				{
					this.sendCommandAck(obj, { result });
				});
			}
		} else
		{
			console.error("RemoteAgent: Received navigate request but no handler is set!");
			return false;
		}
	}

	/** Respond to the monitor command */
	respondToMonitor(obj: RemoteAgentMonitorEvent)
	{
		if (obj.monitor_type === 'debug')
		{
			// TODO: Redirect console.log
		}

		if (obj.monitor_type === 'off')
		{
			RemoteAgent.wantUpdates = false;
			this.sendCommandAck(obj, {});
			return;
		}

		RemoteAgent.wantUpdates = true;
		this.sendCommandAck(obj, {});
	}

	/** Handler for the Login remote command.
	Will call the login handler that was set and send the result back.*/
	respondToLogin(obj: RemoteAgentLoginEvent)
	{
		RemoteAgent.wantUpdates = true;

		if (this.handlerLogin)
		{
			console.log("RemoteAgent: Received login request:", obj);
			let p: any = this.handlerLogin(obj);
			if (p && p.then)
			{
				p.then((result: any) =>
				{
					this.sendCommandAck(obj, { result });
				});
			}
		} else
		{
			console.error("RemoteAgent: Received login request but no handler is set!");
			return false;
		}
	}

	/** Handler for the Env command sent from control.  Contains the project name and environment */
	respondToSetEnv(obj: any)
	{
		RemoteAgent.wantUpdates = true;
		DataServices.setEnvironment(obj.env);
		DataServices.setProjectName(obj.project);
		this.evRemoteSetEnvironment.emit(obj.env);
		this.evRemoteSetProject.emit(obj.project);
		this.sendCommandAck(obj);
	}

	/** Remote has requested a copy of all logic values currently set. */
	respondToGetLogic(obj: any)
	{
		RemoteAgent.wantUpdates = true;

		if (this.currentPage)
		{
			this.sendCommandAck(obj, this.currentPage.logic.values);
		} else
		{
			this.sendCommandAck(obj, { error: 'no page set' });
		}
	}

	/** Remote has requested a logic value be set */
	respondToSetLogic(obj: any)
	{
		if (this.currentPage)
		{
			this.sendCommandAck(obj, this.currentPage.logic.values);
		} else
		{
			this.sendCommandAck(obj, { error: 'no page set' });
		}
	}

	/** Send a confirmation ack back to the control bot that a command was processed */
	sendCommandAck(obj: any, options?: any)
	{
		let response: any = {
			id: obj.seq
		};

		if (options)
			Object.assign(response, options);

		this.an_chan?.publish('ack', DataTools.encodeObjectCompressed(response));
	}

	/** Send a command to a specific remote session */
	sendCommandToSession(commandName: string, target_session: string, options: any)
	{
		return new Promise<boolean>((resolve, reject) =>
		{

			// Get a sequence number for the command we are sending
			const mySequence = this.commandNum++;

			// Store the callback for the response
			this.commandCallback[mySequence] = resolve;

			this.timerCallback[mySequence] = setTimeout(() =>
			{
				// TODO: This will leave an orphan value in commandCallback using memory
				if (this.commandCallback[mySequence])
					this.commandCallback[mySequence](false);
			}, 5000);

			let obj: any = {
				session_id: target_session,
				seq: mySequence
			};

			Object.assign(obj, options);
			this.cc_chan?.publish(commandName, DataTools.encodeObjectCompressed(obj));
		});
	}

	/** Ask a given session to change the environment / session */
	async sendCommandGetLogic(target_session: string)
	{
		return this.sendCommandToSession(RemoteAgent.commandGetLogic, target_session, {
		});
	}

	/** Ask a given session to change the environment / session */
	async sendCommandSetLogic(target_session: string, varName: string, value: any)
	{
		return this.sendCommandToSession(RemoteAgent.commandSetLogic, target_session, {
			varName,
			value
		});
	}

	/** Ask a given session to change the environment / session */
	async sendCommandEnv(target_session: string, envName: string, projectName: string)
	{
		return this.sendCommandToSession(RemoteAgent.commandSetEnv, target_session, {
			env: envName,
			project: projectName,
		});
	}

	/** Tell the remote to login as a participant */
	async sendCommandLoginParticipant(target_session: string, pid: string, password: string)
	{
		return this.sendCommandToSession(RemoteAgent.commandLogin, target_session, {
			login_type: 'participant',
			pid,
			password,
		});
	}

	/** Tell the remote to Navigate to a page.  The remove agent should send an ack once the page
	is fully loaded and executed */
	async sendCommandNavigatePage(target_session: string, page: string)
	{
		// Text replacement only if another page is already loaded with a logic engine.
		if (this.currentPage && this.currentPage.logic)
			page = this.currentPage.logic.processTextReplacement(page);

		return this.sendCommandToSession(RemoteAgent.commandNavigate, target_session, <RemoteAgentNavigateEvent>{
			nav_type: 'page',
			page
		});
	}

	/** Tell the remote to login as an admin user */
	async sendCommandLoginUser(target_session: string, user: string, password: string)
	{
		return this.sendCommandToSession(RemoteAgent.commandLogin, target_session, {
			login_type: 'user',
			user,
			password,
		} as RemoteAgentLoginEvent);
	}

	/** Called when we receive the Who command.  That command is asking for all clients to announce themselves */
	respondToCommandHeartbeat(obj: any)
	{
		this.ping();
	}

	/** Send a control command requesting that all connected clients announce themselves */
	async sendHeartbeatRequest()
	{
		if (!RemoteAgent.enabled || !this.cc_chan) return;

		this.cc_chan.publish(RemoteAgent.commandWho, DataTools.encodeObjectCompressed({
			version,
		}));
	}

}
